import { Link } from "wouter";
import { Loader2, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { RecordPanel } from "@/components/record/RecordFrame";
import type { FunnelCounts } from "@/components/record/facts/clientFacts";
import { useUnlinkProject, type ProjectFull } from "@/hooks/useProjects";
import { openMilestones, projectProgress } from "@shared/clientProgress";
import { cn } from "@/lib/utils";
import { MilestoneRow } from "./MilestoneRow";
import { SUBHEAD } from "./clientStyles";

const FUNNEL: [Exclude<keyof FunnelCounts, "total" | "passed">, string][] = [
  ["teasers", "Teaser"], ["ndas", "NDA"], ["cims", "CIM"], ["iois", "IOI"], ["lois", "LOI"],
];

/** Where the engagement stands: the project's phases, the buyer funnel, and the next open milestones. */
export function ProcessPanel({ companyId, project, loading, error, onRetry, funnel, today, onOpenTracker, onCreateProject, onLinkProject, onChangeProject }: {
  companyId: string;
  project: ProjectFull | null | undefined;
  loading: boolean;
  error: boolean;
  onRetry: () => void;
  funnel: FunnelCounts | null;
  today: string; // YYYY-MM-DD in Central
  onOpenTracker: () => void;
  onCreateProject: () => void;
  onLinkProject: () => void;
  onChangeProject: () => void;
}) {
  const unlink = useUnlinkProject(companyId);
  const progress = project ? projectProgress(project.phases, project.milestones) : null;
  const next = project ? openMilestones(project.milestones, 5) : [];

  return (
    <RecordPanel
      title="Process"
      action={project && (
        <span className="flex items-center gap-3 text-xs font-semibold">
          <Link href={`/projects/${project.id}`} className="text-primary-text hover:underline">Open project</Link>
          <button type="button" onClick={onChangeProject} className="text-muted-foreground hover:text-foreground">Change</button>
          <button
            type="button"
            disabled={unlink.isPending}
            onClick={() => {
              if (window.confirm(`Unlink "${project.name}" from this client? The project itself is not deleted.`)) unlink.mutate(project.id);
            }}
            className="text-destructive hover:underline disabled:opacity-50"
          >
            Unlink
          </button>
        </span>
      )}
    >
      {loading ? (
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      ) : error ? (
        <p className="text-[13px] text-muted-foreground">
          Couldn't load the project.{" "}
          <button type="button" onClick={onRetry} className="font-semibold text-primary-text hover:underline">Retry</button>
        </p>
      ) : !project || !progress ? (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-[13px] text-muted-foreground">No project yet. A project tracks this engagement's milestones.</p>
          <div className="flex flex-wrap gap-1.5">
            <Button size="sm" variant="outline" onClick={onLinkProject}>Link existing project</Button>
            <Button size="sm" className="gap-1" onClick={onCreateProject}><Plus className="h-3.5 w-3.5" />Create project</Button>
          </div>
        </div>
      ) : progress.phases.length > 0 ? (
        <div>
          <div className="mb-1.5 flex flex-wrap items-baseline justify-between gap-2 text-[13px]">
            <span className="font-semibold">{progress.currentIndex < 0 ? "All phases complete" : `Phase: ${progress.phases[progress.currentIndex].name}`}</span>
            <span className="text-xs tabular-nums text-muted-foreground">{progress.overall}% complete</span>
          </div>
          <div className="flex gap-1">
            {progress.phases.map((phase, idx) => (
              <Tooltip key={phase.id}>
                <TooltipTrigger asChild>
                  <div className="h-2 flex-1 cursor-default overflow-hidden rounded-full bg-muted">
                    <div
                      className={cn(
                        "h-full rounded-full transition-all",
                        phase.progress >= 100 ? "bg-green-500" : idx === progress.currentIndex ? "bg-primary" : "bg-muted-foreground/20",
                      )}
                      style={{ width: `${Math.min(phase.progress, 100)}%` }}
                    />
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="font-medium">{phase.name}</p>
                  <p className="text-xs">{phase.progress}% complete</p>
                </TooltipContent>
              </Tooltip>
            ))}
          </div>
          <div className="mt-1 flex justify-between gap-1">
            {progress.phases.map((phase) => (
              <span key={phase.id} className="flex-1 truncate text-center text-[10px] text-muted-foreground" title={phase.name}>{phase.name}</span>
            ))}
          </div>
        </div>
      ) : (
        <p className="text-[13px] text-muted-foreground">This project has no phases. {progress.overall}% of its milestones are done.</p>
      )}

      <div className="mt-3 border-t pt-2.5">
        <div className="mb-1.5 flex items-center justify-between gap-2">
          <h4 className={SUBHEAD}>Buyers</h4>
          <button type="button" onClick={onOpenTracker} className="text-xs font-semibold text-primary-text hover:underline">Open Go-to-Market</button>
        </div>
        {!funnel || funnel.total === 0 ? (
          <p className="text-[13px] text-muted-foreground">No buyers on the tracker yet.</p>
        ) : (
          <>
            <div className="grid grid-cols-5 overflow-hidden rounded-md border text-center">
              {FUNNEL.map(([key, label]) => (
                <div key={key} className="px-1 py-1.5 shadow-[1px_0_0_0_var(--color-border)]">
                  <div className="text-[15px] font-semibold tabular-nums">{funnel[key]}</div>
                  <div className="text-[10.5px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
                </div>
              ))}
            </div>
            <p className="mt-1 text-xs text-muted-foreground">{funnel.total} on the tracker{funnel.passed ? ` · ${funnel.passed} passed` : ""}</p>
          </>
        )}
      </div>

      {project && (
        <div className="mt-3 border-t pt-2.5">
          <h4 className={cn(SUBHEAD, "mb-1 flex items-center gap-1.5")}>
            Next milestones <span className="font-medium normal-case tracking-normal tabular-nums">{next.length}</span>
          </h4>
          {next.length === 0 ? (
            <p className="text-[13px] text-muted-foreground">{project.milestones.length ? "Every milestone is done." : "No milestones on this project yet."}</p>
          ) : (
            next.map((ms) => <MilestoneRow key={ms.id} milestone={ms} projectId={project.id} companyId={companyId} today={today} />)
          )}
        </div>
      )}
    </RecordPanel>
  );
}
